/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Landmark, ShieldAlert, Loader2, ExternalLink, RefreshCw, CheckCircle2, AlertTriangle, Info, ArrowRight, Wallet } from 'lucide-react';
import { startConnectOnboarding, getConnectStatus, getConnectDashboardLink } from '@/lib/telemedicine';

const stateMeta = {
  ready:      { label: 'Attivo',           cls: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30', icon: CheckCircle2 },
  pending:    { label: 'In verifica',      cls: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30',         icon: Loader2 },
  incomplete: { label: 'Da completare',    cls: 'bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-500/30',     icon: AlertTriangle },
  none:       { label: 'Non attivato',     cls: 'bg-muted text-muted-foreground border-border',                                    icon: ShieldAlert },
};

const deriveState = (s) => {
  if (!s || !s.account_id) return 'none';
  if (s.ready) return 'ready';
  if (s.details_submitted && (!s.charges_enabled || !s.payouts_enabled)) return 'pending';
  return 'incomplete';
};

const humanReq = (r) => {
  const map = {
    'business_profile.url': 'URL sito professionale',
    'business_profile.mcc': 'Categoria attività',
    'external_account': 'IBAN / conto per pagamenti',
    'individual.first_name': 'Nome',
    'individual.last_name': 'Cognome',
    'individual.dob.day': 'Data di nascita',
    'individual.address.line1': 'Indirizzo di residenza',
    'individual.id_number': 'Codice fiscale',
    'individual.verification.document': 'Documento di identità',
    'tos_acceptance.date': 'Accettazione termini Stripe',
  };
  return map[r] || r;
};

export const StripeConnectCard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [opening, setOpening] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (fresh = false) => {
    if (fresh) setRefreshing(true);
    try {
      const s = await getConnectStatus();
      setStatus(s);
    } catch (e) {
      // Silent — endpoint may be 502 if Stripe key missing, but we still show card
      setStatus({ account_id: null });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(false); }, []);

  const onStartOnboarding = async () => {
    setStarting(true);
    try {
      const { url } = await startConnectOnboarding(window.location.origin + '/telemedicine');
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossibile avviare l’onboarding');
      setStarting(false);
    }
  };

  const openDashboard = async () => {
    setOpening(true);
    try {
      const { url } = await getConnectDashboardLink();
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Dashboard non disponibile');
    } finally {
      setOpening(false);
    }
  };

  const state = deriveState(status);
  const meta = stateMeta[state];
  const StateIcon = meta.icon;

  return (
    <Card data-testid="stripe-connect-card">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="font-display flex items-center gap-2">
              <Landmark className="h-5 w-5 text-primary" /> Pagamenti dai pazienti
            </CardTitle>
            <CardDescription>Configura il tuo conto Stripe Connect per ricevere i pagamenti delle visite.</CardDescription>
          </div>
          <Badge variant="outline" className={meta.cls} data-testid="connect-state-badge">
            <StateIcon className={`h-3 w-3 mr-1 ${state === 'pending' ? 'animate-spin' : ''}`} />
            {meta.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <Skeleton className="h-24" />
        ) : (
          <>
            {state === 'none' && (
              <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-warning/40 bg-warning/5 p-4">
                <div className="flex items-start gap-3">
                  <Wallet className="h-5 w-5 text-warning mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm">Attiva Stripe Connect per essere pagato</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Onboarding rapido (5–10 minuti) tramite Stripe Express: verifica identità + IBAN. I compensi delle visite pagate ti arrivano direttamente sul tuo conto.
                    </div>
                    <Button onClick={onStartOnboarding} disabled={starting} className="mt-3" data-testid="connect-start-btn">
                      {starting ? <><Loader2 className="h-4 w-4 animate-spin" /> Apertura Stripe…</> : <><ArrowRight className="h-4 w-4" /> Attiva pagamenti</>}
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}

            {(state === 'incomplete' || state === 'pending') && (
              <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
                <Alert className="border-amber-500/40 bg-amber-500/5">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-xs">
                    {state === 'pending'
                      ? 'Il tuo profilo Stripe è in verifica. Potrebbe richiedere fino a 24h. Nel frattempo puoi già accettare visite, ma i pagamenti saranno rilasciati solo quando la verifica sarà completata.'
                      : 'Il tuo profilo Stripe è incompleto. Completa i dati richiesti per iniziare a ricevere pagamenti.'}
                  </AlertDescription>
                </Alert>

                {status?.requirements_due?.length > 0 && (
                  <div className="rounded-xl border border-border/60 p-3">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-2">Dati mancanti ({status.requirements_due.length})</div>
                    <ul className="text-xs space-y-1">
                      {status.requirements_due.slice(0, 8).map((r) => (
                        <li key={r} className="flex items-start gap-2">
                          <span className="mt-1.5 inline-block w-1 h-1 rounded-full bg-amber-500 flex-shrink-0" />
                          <span>{humanReq(r)}</span>
                        </li>
                      ))}
                      {status.requirements_due.length > 8 && (
                        <li className="text-muted-foreground">+{status.requirements_due.length - 8} altri campi</li>
                      )}
                    </ul>
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  <Button onClick={onStartOnboarding} disabled={starting} data-testid="connect-resume-btn">
                    {starting ? <><Loader2 className="h-4 w-4 animate-spin" /> Apertura…</> : <><ExternalLink className="h-4 w-4" /> Completa onboarding</>}
                  </Button>
                  <Button variant="outline" onClick={() => load(true)} disabled={refreshing} data-testid="connect-refresh-btn">
                    {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                    Aggiorna stato
                  </Button>
                </div>
              </motion.div>
            )}

            {state === 'ready' && (
              <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-border/60 p-3">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Addebito visite</div>
                    <div className="flex items-center gap-1.5 mt-1 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Attivo
                    </div>
                  </div>
                  <div className="rounded-xl border border-border/60 p-3">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Payout su IBAN</div>
                    <div className="flex items-center gap-1.5 mt-1 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Attivo
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={openDashboard} disabled={opening} data-testid="connect-dashboard-btn">
                    {opening ? <><Loader2 className="h-4 w-4 animate-spin" /> Apertura…</> : <><ExternalLink className="h-4 w-4" /> Apri dashboard Stripe</>}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => load(true)} disabled={refreshing}>
                    {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                    Sincronizza
                  </Button>
                </div>
              </motion.div>
            )}

            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription className="text-xs">
                Commissione piattaforma: 10% (già inclusa nel prezzo visita). I compensi vengono rilasciati sul tuo IBAN entro 2–7 giorni lavorativi da Stripe.
              </AlertDescription>
            </Alert>
          </>
        )}
      </CardContent>
    </Card>
  );
};
